import type { CityConfig } from "@/pages/city-template/types";

export const salemConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Marion County Clerk Vital Records", desc: "Obtain vital records through the Marion County Clerk's office in Salem.", url: "https://www.co.marion.or.us/CO/" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Salem",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "salem",
  route: "/cities/salem",
  title: "Emergency Passport Salem, OR — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Salem, OR — as fast as 24 hours. Fast passport renewal, same-day help near Salem, and child passport expediting for Marion and Polk County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Salem, Oregon",
  heroSubtitle: "Passport expired before your trip? Need a passport in 7 days from Salem? Fast Passport Center helps Marion and Polk County travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Salem%20Oregon%20state%20capitol%20building%20with%20golden%20pioneer%20statue%20atop%20dome%2C%20surrounded%20by%20lush%20green%20Willamette%20Valley%20farmland%2C%20historic%20brick%20buildings%20on%20Capitol%20Mall%2C%20Mt%20Jefferson%20visible%20in%20hazy%20distance%2C%20warm%20afternoon%20golden%20light%2C%20professional%20landscape%20photography%20style%2C%20vibrant%20Pacific%20Northwest%20colors%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=salem-hero-01&orientation=landscape",
  heroImageAlt: "Salem Oregon state capitol building and Willamette Valley landscape",
  geoPosition: "44.9429;-123.0351",
  geoPlacename: "Salem, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "Approximately 215 miles, 3.5 hours via I-5",
  },
  nearbyCitiesZips: [
    { name: "Salem", zips: "97301–97317" },
    { name: "Keizer", zips: "97307" },
    { name: "Woodburn", zips: "97071" },
    { name: "Silverton", zips: "97381" },
    { name: "Stayton", zips: "97383" },
    { name: "Dallas", zips: "97338" },
    { name: "Monmouth", zips: "97361" },
    { name: "Independence", zips: "97351" },
    { name: "McMinnville", zips: "97128" },
    { name: "Mt Angel", zips: "97362" },
    { name: "Aumsville", zips: "97325" },
    { name: "Turner", zips: "97392" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Salem or Keizer in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Salem", desc: "FedEx and UPS locations in Salem offer overnight shipping — documents arrive at our processing center by next morning." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission — especially critical when you need a rush passport service in Salem with no local regional agency." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting or Seattle drive required." },
  ],
  testimonials: [
    {
      name: "Stephanie R.",
      location: "Salem, OR",
      rating: 5,
      text: "I'm a state government employee who got assigned to an international trip with 10 days notice. My passport had expired. Fast Passport Center processed my renewal in 6 days — I shipped from the FedEx off Mission Street. Saved my professional life.",
      service: "Passport Renewal",
    },
    {
      name: "Carlos M.",
      location: "Keizer, OR",
      rating: 5,
      text: "We were going to Costa Rica for our anniversary and my wife noticed her passport expired a week before the trip. Fast Passport Center renewed it in 5 days. From Keizer — overnight FedEx, no problem. Will never use anyone else.",
      service: "Emergency Passport",
    },
    {
      name: "Hannah J.",
      location: "Woodburn, OR",
      rating: 5,
      text: "Needed a passport for my 8-year-old for a Mexico cruise. Being new to the process, I was overwhelmed. Fast Passport Center's checklist and guidance made it completely clear what we needed. Both parents signed off and we had the passport in 8 days.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "salem-main-post",
      name: "Salem Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1050 25th St SE",
      city: "Salem",
      state: "OR",
      zip: "97301",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "South Salem",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Salem. Appointment required — book online at usps.com. Photo services typically available.",
      icon: "ri-mail-line",
    },
    {
      id: "salem-commercial",
      name: "Salem Commercial Street Post Office",
      type: "USPS Passport Acceptance",
      address: "1050 Commercial St SE",
      city: "Salem",
      state: "OR",
      zip: "97302",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Central Salem — Commercial Street corridor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Central Salem location. Confirm appointment availability and photo services.",
      icon: "ri-mail-line",
    },
    {
      id: "marion-county-clerk",
      name: "Marion County Clerk — Passport Acceptance",
      type: "County Passport Acceptance",
      address: "100 High St NE",
      city: "Salem",
      state: "OR",
      zip: "97301",
      phone: "503-588-5225",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Salem — Marion County Courthouse",
      website: "https://www.co.marion.or.us/CO/Pages/CountyClerk.aspx",
      websiteLabel: "Marion County Clerk",
      notes: "County clerk passport acceptance at the Marion County courthouse. Call ahead to confirm availability and schedule an appointment.",
      icon: "ri-government-line",
    },
    {
      id: "keizer-post",
      name: "Keizer Post Office",
      type: "USPS Passport Acceptance",
      address: "4040 River Rd N",
      city: "Keizer",
      state: "OR",
      zip: "97307",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Keizer — about 5 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Keizer option for north Salem metro residents. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "woodburn-post",
      name: "Woodburn Post Office",
      type: "USPS Passport Acceptance",
      address: "130 S Cascade Dr",
      city: "Woodburn",
      state: "OR",
      zip: "97071",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Woodburn — about 18 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Woodburn and north Marion County option. Confirm appointment and photo service availability.",
      icon: "ri-mail-line",
    },
    {
      id: "dallas-post",
      name: "Dallas Post Office",
      type: "USPS Passport Acceptance",
      address: "183 SW Academy St",
      city: "Dallas",
      state: "OR",
      zip: "97338",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Dallas, Polk County — about 15 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Polk County option for western Salem metro area residents. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Salem before international travel?",
      a: "Yes. Through Fast Passport Center, Salem travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on eligibility and travel documentation. Salem does not have a regional passport agency — Fast Passport Center is the most practical solution for most Marion County travelers.",
    },
    {
      q: "How fast can I renew my passport in Salem?",
      a: "Fast passport renewal in Salem is available through Fast Passport Center in as little as 3 business days with our expedited service. Most Salem-area adults can renew entirely by mail — start online, complete documents at home, and ship overnight from any FedEx or UPS in Salem. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires soon and I have travel coming up?",
      a: "If your passport expires soon and you have travel booked, don't wait. Many countries require 3–6 months of validity beyond your return date. Fast Passport Center can expedite your renewal with predictable turnaround times — and our document review catches issues before they cause delays.",
    },
    {
      q: "Can I get a same-day passport appointment near Salem?",
      a: "Same-day or emergency appointments at the nearest regional passport agency (Seattle, about 230 miles north) are restricted to documented life-or-death emergencies. For most urgent passport needs in Salem, Fast Passport Center is the faster and more predictable option — no long drive required.",
    },
    {
      q: "Can I expedite a child passport in Salem?",
      a: "Yes. Child passport expediting in Salem is possible through Fast Passport Center. Minors under 16 require an in-person acceptance step with parental consent — both parents typically need to be present or provide documentation. We guide Salem families through every step with a clear checklist.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "Report the lost passport immediately using Form DS-64 and apply for a replacement. Fast Passport Center guides Salem travelers through this process. A lost passport cannot be used even if found later, and replacement applications require extra documentation. Contact us right away to start the process.",
    },
    {
      q: "Does Salem have a passport agency?",
      a: "Salem, Oregon does not have a regional passport agency. As Oregon's state capital, Salem is a busy hub — but the nearest regional agencies are in Seattle (about 230 miles north) and San Francisco (about 600 miles south). Fast Passport Center provides Salem residents with the same expedited results without requiring them to travel.",
    },
    {
      q: "How do Salem state government employees get expedited passports for work travel?",
      a: "State government employees needing passports for work-related international travel can use Fast Passport Center's expedited service. Start online, ship documents from FedEx or UPS in Salem, and most applications are processed in 2–9 business days. We also handle urgent passport application needs with fast turnaround.",
    },
    {
      q: "What is the fastest passport renewal option in Salem?",
      a: "The fastest passport renewal in Salem is through Fast Passport Center's expedited courier service — processing in as little as 3 business days. This is significantly faster than standard USPS processing (6–13 weeks). Start online, ship overnight, and we handle submission through our reserved agency access.",
    },
    {
      q: "Can I get rush passport service if I need a passport in 7 days in Salem?",
      a: "Yes. If you need a passport in 7 days from Salem, contact Fast Passport Center immediately. Our expedited options can process applications in as little as 2–9 business days, and emergency service may be available for qualifying situations. The sooner you start, the more options you have.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Salem Commercial St", address: "3510 Commercial St SE, Salem, OR 97302", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Salem Lancaster Dr", address: "2555 Lancaster Dr NE, Salem, OR 97305", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Mission St", address: "2985 Mission St SE, Salem, OR 97302", hours: "Mon–Fri 7 AM – 9 PM, Sat–Sun 8 AM – 6 PM" },
    { name: "The UPS Store — Salem", address: "1695 Edgewater St NW, Salem, OR 97304", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
    { name: "Walmart Supercenter — Salem", address: "5195 Silverton Rd NE, Salem, OR 97305", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Keizer", address: "4890 River Rd N, Keizer, OR 97307", hours: "Daily 8 AM – 9 PM" },
    { name: "Costco — Salem", address: "1550 Wal-Mart Dr NE, Salem, OR 97301", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "Sam's Club — Salem", address: "2600 Fisher Rd NE, Salem, OR 97305", hours: "Mon–Sat 10 AM – 8 PM, Sun 10 AM – 6 PM" },
  ],
  dmvLocations: [
    {
      name: "Salem DMV — Oregon Driver & Motor Vehicle Services",
      address: "1905 Fairgrounds Rd NE, Salem, OR 97301",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Main Salem DMV. Driver licenses, Oregon REAL ID, ID cards. Appointment recommended — book online at oregondmv.com.",
      featured: true,
    },
    {
      name: "Woodburn DMV Office",
      address: "535 N Pacific Hwy, Woodburn, OR 97071",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "North Marion County option. Appointment recommended.",
      featured: false,
    },
    {
      name: "Dallas DMV — Polk County",
      address: "1065 SW Main St, Dallas, OR 97338",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Polk County / western Salem option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "SLE",
      name: "Salem Municipal Airport — McNary Field",
      distance: "Local airport — 4 miles south of downtown",
      address: "1880 SE Aviation Way, Salem, OR 97301",
      note: "Salem Municipal handles charter and regional air service. Most commercial and international flights depart from Portland International (PDX).",
      flightUrl: "https://www.cityofsalem.net/City_Projects/Airport",
      flightLabel: "Salem Airport Website",
      featured: true,
    },
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international hub — about 50 miles north (1 hour)",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Most Salem-area travelers use PDX for international and major domestic flights. Valid passport required for all international departures — ensure yours is ready well before your PDX departure.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Need a passport in 7 days from Salem? Our emergency courier service handles urgent, last-minute travel from Marion and Polk County — as fast as 24 hours.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast passport renewal for Salem residents — no office visit required for most adult renewals. Ship documents from Salem and we handle everything.",
      bullets: ["Eligible for expedited processing", "Ship from FedEx or UPS in Salem", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost passport before a trip? Fast guidance on DS-64 and urgent replacement for Salem and Willamette Valley travelers.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Urgent child passport for Salem families — clear guidance through parental consent and the in-person acceptance requirement.",
      bullets: ["Parental consent guidance included", "Clear checklist provided", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application for Salem residents — we guide you through the acceptance clerk process and handle the expedited submission.",
      bullets: ["Step-by-step guidance", "Acceptance clerk directions", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered State Dept. courier serving Salem, Keizer, Woodburn, and Marion and Polk Counties — rush passport service without the Seattle drive.",
      bullets: ["Registered State Dept. courier", "Reserved daily submission slots", "Not affected by shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Salem, Oregon travelers needing a passport as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport services for Salem, OR residents needing faster than standard government processing times." },
    { name: "Passport Renewal", description: "Fast passport renewal for Salem residents in Marion and Polk County, Oregon." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Salem, OR travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Salem, Oregon families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport service serving Salem and the Willamette Valley, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long", description: "Standard processing out of Salem runs 6–13 weeks or more during busy government periods. If your international trip is within 10 weeks, you need expedited service — don't wait." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Even at name-brand pharmacies in Salem, photos sometimes don't meet spec. Always ask the technician to double-check before you leave." },
    { icon: "ri-file-damage-line", title: "Form Errors", description: "A missing signature or incorrect date sends your application back — losing weeks. Our document review catches these before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Hospital records and photocopies are not accepted — you need a certified copy with a raised seal. We specify exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Relying on Standard USPS Speed", description: "If you're flying from PDX in under 10 weeks, USPS standard processing isn't going to cut it. Use expedited courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatches", description: "Your passport must match your airline ticket exactly — middle name, suffix, hyphen. These small mismatches cause big problems at the international gate." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that matches your Salem travel date. Complete your secure order in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Checklist", description: "We confirm your details and send a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Overnight from Salem", description: "Drop off at FedEx on Mission St or UPS in Salem. Documents arrive at our processing center next morning." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through reserved agency slots — no public appointment wait, no Seattle drive needed." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Ready", description: "Your passport is completed within your selected timeframe and shipped back directly to you in Salem." },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive 215 miles to Seattle via I-5", fpc: "Ship documents overnight from Salem" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Total Time Commitment", agency: "Full day driving + waiting at agency", fpc: "10 minutes online + overnight shipping" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Eugene", route: "/eugene" },
    { name: "Expedited passport near Keizer", route: "#" },
    { name: "Rush passport service for Willamette Valley travelers", route: "/oregon" },
    { name: "Passport agency near McMinnville", route: "#" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship documents overnight from FedEx on Mission St or the UPS Store in Salem. Documents arrive at our processing center next morning — no travel to Portland or Seattle required.",
  fpcOfficeDistance: "Serving Salem, Keizer, and all of Marion and Polk County remotely",
  serviceAreaIntro: "Whether you are in Salem, Keizer, Woodburn, Silverton, Dallas, or anywhere in Marion or Polk County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight from Salem, and we handle everything.",
  nearbyCitiesIntro: "We serve all Salem metro zip codes and surrounding Willamette Valley communities. Ship overnight from Salem and your documents are at our processing center next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Salem as simple as possible — most clients never leave their neighborhood.",
  mistakesIntro: "These are the most common mistakes Salem and Willamette Valley travelers make when applying for passports. Fast Passport Center reviews every document before submission.",
  testimonialsIntro: "Real reviews from Salem, Keizer, and Woodburn travelers who needed their passports fast and trusted Fast Passport Center to deliver.",
  faqIntro: "The most common questions Salem and Marion County travelers ask about rush passport services and emergency passport renewal.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
};